var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html, nothing } from 'lit';
import { property, query, queryAll } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { customElement, stringDecoder, PICBase, PIC, base64DecoderJSON, submitEventHandler, PICAction, } from '../Internals';
const DEFAULT_POSITIONS = 25;
const translateObj = {
    engb: {
        reserve: 'Reserve',
        cancelReserve: 'Cancel reservation',
        cancelShunting: 'Cancel shunting',
        executeShunting: 'Execute shunting',
        shuntToTrain: 'Shunt to train',
        track: 'Track',
        flipWagons: 'Flip wagon sequence',
        arrTime: 'Arriving at',
        depTime: 'Departing at',
    },
    dadk: {
        reserve: 'Reserver',
        cancelReserve: 'Annuller reservation',
        cancelShunting: 'Aflys rangering',
        executeShunting: 'Udfør rangering',
        shuntToTrain: 'Rangér til tog',
        track: 'Spor',
        flipWagons: 'Vend vognsekvens om',
        arrTime: 'Ankomst kl',
        depTime: 'Afgang kl',
    },
    dede: {
        reserve: 'Reservieren',
        cancelReserve: 'Reservierung aufheben',
        cancelShunting: 'Rangieren abbrechen',
        executeShunting: 'Rangieren ausführen',
        shuntToTrain: 'Zum Zug rangieren',
        track: 'Gleis',
        flipWagons: 'Wagenreihenfolge umkehren',
        arrTime: 'Ankunft um',
        depTime: 'Abfahrt um',
    },
};
let PICShuntView = class PICShuntView extends PICBase {
    set height(iheight) {
        let heightParser = iheight !== null && iheight !== void 0 ? iheight : '80vh';
        this._height = heightParser;
        this.requestUpdate();
    }
    get height() {
        return this._height;
    }
    set width(iwidth) {
        let widthParser = iwidth !== null && iwidth !== void 0 ? iwidth : '80vw';
        this._width = widthParser;
        this.requestUpdate();
    }
    get width() {
        return this._width;
    }
    constructor() {
        super();
        this.stationName = '';
        this.locationCode = '';
        this.countryCode = '';
        this._height = '80vh';
        this._width = '80vw';
        this._submitVal = '';
        this.infoVisible = false;
        this.wagons = [];
        this.shuntingPlans = [];
        this.tracks = [];
        this.positions = [];
        this.trains = [];
        this.selectedCells = [];
        this.shuntingRecipients = [];
    }
    static get styles() {
        const defaultStyle = css `
        :host{
            --shuntview-width: 100%;
            --shuntview-height: 100%;
            /* Header surface: the raised/lighter tone, not the navy nav
               surface - this is a data-grid header, not a page header. */
            --header-bg-color: var(--pic-tableAlternateRowColor);
            --header-color: var(--pic-tableMediumColor);
            /* Drag-over feedback keeps its own distinct navy tint, decoupled
               from --header-bg-color, so it stays visible against a header
               that's now close in lightness to the component background. */
            --bg-color-dragover: var(--pic-tableMediumBgColor);
            --bg-color-empty: var(--pic-backgroundColor, #ffffff);
            --bg-color-reserved: var(--pic-statusDoneColor);
            --bg-color-wagon: var(--pic-statusNewColor);
            --bg-color-wagon-dmg-mild: var(--pic-statusDepartedColor);
            --bg-color-wagon-dmg-moderate: var(--pic-statusSettledColor);
            --bg-color-wagon-dmg-severe: var(--pic-statusCanceledColor);
            --bg-color-planned: rgb(from var(--bg-color-wagon) r g b / 0.5);
            --color-planned: var(--pic-onSurfaceDisabledColor);
            /* Component/table background matches the page background rather
               than the raised surface tone - cell definition now comes from
               --border-color, not from a lighter fill behind the grid. */
            --bg-color-track-gap: var(--pic-backgroundColor, #f0f5fc);
            --border-color: var(--pic-borderStrongColor, var(--pic-outline));
            --bg-color-no-drop: var(--pic-statusCanceledColor);
        }
        .outerContainer {
            max-width: var(--shuntview-width);
            max-height: var(--shuntview-height);
            overflow-x: auto;
            overflow-y: auto;
            background-color: var(--pic-backgroundColor, #ffffff);
        }
        .info-toggle {
            display: block;
            margin-left: auto;
            margin-right: 0;
        }
        .table {
            width: 100%;
            table-layout: fixed;
            border-collapse: separate;
            border-spacing: 0px;
            background-color: var(--bg-color-track-gap);
        }
        .table th,
        .table td {
            user-select: none;
            width: 80px;
            height: 20px;
        }
        tbody tr td.header {
            text-align: center;
        }
        .track-gap {
            background-color: var(--bg-color-track-gap);
        }
        .cell {
            cursor: pointer;
            text-align: center;
        }
        .cell span {
            display: inline-block;
            background-color: var(--bg-color-empty);
            border: 1px solid var(--border-color);
            width: 75px;
            height: 20px;
            margin: 0 0 0 5px;
            vertical-align: middle;
        }
        .cell.reserved span{
            background-color: var(--bg-color-reserved);
        }
        .cell.planned span{
            background-color: var(--bg-color-planned);
            color: var(--color-planned);
        }
        .cell.wagon span{
            background-color: var(--bg-color-wagon);
        }
        .cell.wagon.dmg-mild:not(.dmg-moderate):not(.dmg-severe) span{
            background-color: var(--bg-color-wagon-dmg-mild);
        }
        .cell.wagon.dmg-moderate:not(.dmg-severe) span{
            background-color: var(--bg-color-wagon-dmg-moderate);
        }
        .cell.wagon.dmg-severe span{
            background-color: var(--bg-color-wagon-dmg-severe);
        }
        .cell.selected span {
            outline: 2px solid var(--pic-primaryColor, #1976d2);
            outline-offset: 1px;
        }
        .cell.dragover span {
            background-color: var(--bg-color-dragover);
        }
        .cell.no-drop span {
            background-color: var(--bg-color-no-drop);
        }
        .header {
            background-color: var(--header-bg-color);
            color: var(--header-color);
        }
        .train-row {
            text-align: center;
        }
        .train-block span{
            display: inline-block;
            height: 22px;
            margin: 0 0 0 5px;
            vertical-align: middle;
        }
        .train-block span.train-block-span {
            background-color: var(--bg-color-empty);
            border: 1px solid var(--border-color);
            height: 20px;
            text-overflow: ellipsis;
            white-space: nowrap; 
            overflow: hidden;
            padding-left: 5px;
        }
        `;
        return css `
        ${super.styles}
        ${defaultStyle}
        `;
    }
    set submitValue(value) {
        this._submitVal = value;
    }
    get submitValue() {
        return this._submitVal;
    }
    submitValFromSelectedCells() {
        let selectedValues = '';
        if (this.selectedCells.length > 0) {
            let type = this.selectedCells[0].classList.contains('wagon') ? 'wagon' : this.selectedCells[0].classList.contains('planned') ? 'planned' : 'empty';
            if (type === 'wagon') {
                selectedValues = this.selectedCells.map(c => { var _a, _b; return (_b = (_a = c.item) === null || _a === void 0 ? void 0 : _a.wagonNo) !== null && _b !== void 0 ? _b : ''; }).join('^');
            }
            else if (type === 'planned') {
                selectedValues = this.selectedCells.map(c => { var _a; return (_a = c.getAttribute('data-move-id')) !== null && _a !== void 0 ? _a : ''; }).join('^');
            }
            else if (type === 'empty') { //also handles reserved cells
                selectedValues = this.selectedCells.map(c => { var _a; return (_a = c.getAttribute('data-storepos')) !== null && _a !== void 0 ? _a : ''; }).join('^');
            }
        }
        return selectedValues;
    }
    shuntValFromSelectedCells() {
        let selectedValues = '';
        if (this.selectedCells.length > 0) {
            let type = this.selectedCells[0].classList.contains('wagon') ? 'wagon' : this.selectedCells[0].classList.contains('planned') ? 'planned' : 'empty';
            if (type === 'wagon') {
                selectedValues = this.selectedCells.map(c => {
                    var _a, _b, _c;
                    let storePos = (_a = c.getAttribute('data-storepos')) !== null && _a !== void 0 ? _a : '';
                    let wagonNo = (_c = (_b = c.item) === null || _b === void 0 ? void 0 : _b.wagonNo) !== null && _c !== void 0 ? _c : '';
                    return storePos + ':' + wagonNo;
                }).join('^');
            }
            else if (type === 'planned') {
                selectedValues = this.selectedCells.map(c => {
                    var _a, _b;
                    let storePos = (_a = c.getAttribute('data-storepos')) !== null && _a !== void 0 ? _a : '';
                    let wagonNo = (_b = c.getAttribute('data-move-id')) !== null && _b !== void 0 ? _b : '';
                    return storePos + ':' + wagonNo;
                }).join('^');
            }
        }
        return selectedValues;
    }
    handleTrainData(train) {
        if (typeof train.trainDate === 'string')
            train.trainDate = new Date(train.trainDate);
        if (typeof train.arrDate === 'string')
            train.arrDate = new Date(train.arrDate);
        if (typeof train.depDate === 'string')
            train.depDate = new Date(train.depDate);
        if (train.wagons) {
            train.wagons.forEach(wagon => {
                if (typeof wagon === 'string') {
                    let wagonObj = this.wagons.find(w => w.wagonNo === wagon);
                    if (wagonObj)
                        wagonObj.trainNo = train.trainNo;
                }
            });
        }
    }
    handleMoveData(move) {
        if (typeof move.fromLoc === 'string')
            move.fromLoc = this.parseStorePos(move.fromLoc);
        if (typeof move.toLoc === 'string')
            move.toLoc = this.parseStorePos(move.toLoc);
    }
    checkAndConsumeInlineData() {
        var _a, _b, _c, _d, _e;
        let iData = this.getAttribute('datafromprogress');
        if (iData) {
            let data = base64DecoderJSON(iData);
            this.wagons = (_a = data.Wagons) !== null && _a !== void 0 ? _a : [];
            this.shuntingPlans = (_b = data.ShuntingPlans) !== null && _b !== void 0 ? _b : [];
            this.tracks = (_c = data.Tracks) !== null && _c !== void 0 ? _c : [];
            this.trains = (_d = data.Trains) !== null && _d !== void 0 ? _d : [];
            if (this.wagons.length > 0) {
                this.wagons.forEach(wagon => {
                    if (typeof wagon.storePos === 'string') {
                        wagon.storePos = this.parseStorePos(wagon.storePos);
                    }
                });
            }
            if (this.shuntingPlans.length > 0) {
                this.shuntingPlans.forEach(plan => {
                    if (plan.user !== PIC.getInstance.user)
                        return;
                    plan.moves.forEach(move => {
                        this.handleMoveData(move);
                    });
                });
            }
            if (this.tracks.length > 0) {
                this.tracks.forEach(track => {
                    if (track.positions) {
                        track.positions.forEach(position => {
                            if (!this.positions.includes(position)) {
                                this.positions.push(position);
                            }
                        });
                    }
                });
            }
            if (this.trains.length > 0) {
                this.trains.forEach(train => {
                    this.handleTrainData(train);
                });
            }
            if (typeof data.contextMenu === 'string' && data.contextMenu.length > 0) {
                const decoded = base64DecoderJSON(data.contextMenu);
                this.contextMenu = (_e = decoded.contextMenuTT) !== null && _e !== void 0 ? _e : Object.values(decoded)[0];
            }
            this.requestUpdate();
        }
        this.removeAttribute('datafromprogress');
    }
    connectedCallback() {
        super.connectedCallback();
        this.checkAndConsumeInlineData();
        window.PIC.addInput(this.id_, this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        window.PIC.removeInput(this.id_);
    }
    addCMDListeners() {
        super.addCMDListeners();
        this.addEventListener('update', this.handleCMDUpdate.bind(this));
        this.addEventListener('removeWagons', (e) => this.handleCMDRemoveShuntData('wagon', e));
        this.addEventListener('removePlans', (e) => this.handleCMDRemoveShuntData('plan', e));
        this.addEventListener('removeTrains', (e) => this.handleCMDRemoveShuntData('train', e));
    }
    removeCMDListeners() {
        super.removeCMDListeners();
        this.removeEventListener('update', this.handleCMDUpdate.bind(this));
        this.removeEventListener('removeWagons', (e) => this.handleCMDRemoveShuntData('wagon', e));
        this.removeEventListener('removePlans', (e) => this.handleCMDRemoveShuntData('plan', e));
        this.removeEventListener('removeTrains', (e) => this.handleCMDRemoveShuntData('train', e));
    }
    handleCMDRemoveShuntData(specifier, e) {
        var _a;
        let data = e.detail.data;
        if (specifier === 'wagon') {
            let toRemove = new Set(data.split(',').map(s => s.trim()).filter(s => s.length > 0));
            if (toRemove.size > 0) {
                this.wagons = this.wagons.filter(w => !toRemove.has(w.wagonNo));
            }
        }
        else if (specifier === 'train') {
            let trainNos = data.split(',').map(s => s.trim()).filter(s => s.length > 0);
            for (let i = 0; i < trainNos.length; i++) {
                const trainNo = trainNos[i];
                for (let t = 0; t < this.trains.length; t++) {
                    const train = this.trains[t];
                    if (train.trainNo !== trainNo)
                        continue;
                    if (train.wagons) {
                        for (let w = 0; w < train.wagons.length; w++) {
                            const wno = train.wagons[w];
                            this.wagons = this.wagons.filter(wg => wg.wagonNo !== wno);
                        }
                    }
                }
                this.trains = this.trains.filter(tr => tr.trainNo !== trainNo);
            }
        }
        else if (specifier === 'plan') {
            const parts = data.split('^');
            const userId = parts[0];
            const planIdsPart = (_a = parts[1]) !== null && _a !== void 0 ? _a : '';
            if (userId !== PIC.getInstance.user)
                return;
            const planIds = planIdsPart.split(',').map(s => s.trim()).filter(s => s.length > 0);
            for (let p = 0; p < planIds.length; p++) {
                const id = planIds[p];
                this.shuntingPlans = this.shuntingPlans.filter(pl => pl.id !== id);
            }
        }
        this.requestUpdate();
    }
    handleCMDUpdate(e) {
        this.clearSelection();
        this.submitValue = this.submitValFromSelectedCells();
        let data = e.detail.data;
        if (typeof data === 'string') {
            data = base64DecoderJSON(data);
        }
        if (data.Wagons && data.Wagons.length > 0) {
            data.Wagons.forEach(wagon => {
                if (typeof wagon.storePos === 'string')
                    wagon.storePos = this.parseStorePos(wagon.storePos);
                let existingWagon = this.wagons.find(w => w.wagonNo === wagon.wagonNo);
                if (existingWagon) {
                    Object.assign(existingWagon, wagon);
                }
                else {
                    this.wagons.push(wagon);
                }
            });
        }
        if (data.ShuntingPlans && data.ShuntingPlans.length > 0) {
            data.ShuntingPlans.forEach(plan => {
                if (plan.user !== PIC.getInstance.user)
                    return;
                plan.moves.forEach(move => {
                    this.handleMoveData(move);
                });
                let existingPlan = this.shuntingPlans.find(p => p.id === plan.id);
                if (existingPlan) {
                    Object.assign(existingPlan, plan);
                }
                else {
                    this.shuntingPlans.push(plan);
                }
            });
        }
        if (data.Trains && data.Trains.length > 0) {
            data.Trains.forEach(train => {
                this.handleTrainData(train);
                let existingTrain = this.trains.find(t => t.trainNo === train.trainNo);
                if (existingTrain) {
                    Object.assign(existingTrain, train);
                }
                else {
                    this.trains.push(train);
                }
            });
        }
        this.requestUpdate();
    }
    translateTxt(key) {
        var _a;
        let objKey = PIC.getInstance.userConfig.userLang.replace('-', '').toLowerCase();
        if (!['engb', 'dadk', 'dede'].includes(objKey))
            objKey = 'engb';
        return (_a = translateObj[objKey][key]) !== null && _a !== void 0 ? _a : translateObj.engb[key];
    }
    handleContextMenu(e, wagon, move) {
        var _a, _b, _c, _d;
        let cell = e.composedPath().find((el) => {
            return el instanceof HTMLElement && el.classList.contains('cell');
        });
        Array.from(this.allCells).forEach(c => {
            c.classList.remove('no-drop', 'dragover');
        });
        if (cell) {
            e.preventDefault();
            const context_ = [];
            if (!this.selectedCells.includes(cell)) {
                this.clearSelection();
                this.toggleSelection(cell);
                this.submitValue = this.submitValFromSelectedCells();
            }
            if (!cell.classList.contains('wagon') && !cell.classList.contains('planned')) {
                let isReserved = cell.classList.contains('reserved');
                context_.push({ menuTxt: this.translateTxt(isReserved ? 'cancelReserve' : 'reserve'), eventTxt: isReserved ? 'cancelReserve' : 'reserve' });
            }
            if (cell.classList.contains('planned')) {
                context_.push({ menuTxt: this.translateTxt('cancelShunting'), eventTxt: 'cancelShunting' });
                context_.push({ menuTxt: this.translateTxt('executeShunting'), eventTxt: 'executeShunting' });
            }
            if (wagon === null || wagon === void 0 ? void 0 : wagon.contextItem) {
                const ctxarr = wagon.contextItem.startsWith('%02') ? wagon.contextItem.replace('%02', '').split('%02') : wagon.contextItem.split('%02');
                ctxarr.forEach(elm => {
                    if (!elm.includes('%04'))
                        return;
                    const sub = elm.split('%04');
                    context_.push({ menuTxt: sub[0], eventTxt: sub[1] });
                });
            }
            if (cell.classList.contains('wagon')) {
                if (this.shuntingPlans.length > 0) {
                    let wagonIds = this.selectedCells.map(c => { var _a; return (_a = c.item) === null || _a === void 0 ? void 0 : _a.wagonNo; });
                    let inPlans = new Set(this.shuntingPlans.flatMap(p => p.moves.map(m => m.wagonId)));
                    if (wagonIds.every(id => inPlans.has(id))) {
                        context_.push({ menuTxt: this.translateTxt('executeShunting'), eventTxt: 'executeShunting' });
                        context_.push({ menuTxt: this.translateTxt('cancelShunting'), eventTxt: 'cancelShunting' });
                    }
                }
                if (!(wagon === null || wagon === void 0 ? void 0 : wagon.trainNo) || (wagon === null || wagon === void 0 ? void 0 : wagon.trainNo.length) === 0)
                    context_.push({ menuTxt: this.translateTxt('shuntToTrain'), eventTxt: 'shuntToTrain' });
                // if positions of wagons from selected cells form an unbroken sequence, and none of them have a train number, add flipWagons to context
                if (this.selectedCells.length > 1 && !this.selectedCells.find(c => { var _a; return (_a = c.item) === null || _a === void 0 ? void 0 : _a.trainNo; })) {
                    let sps = this.selectedCells.map(c => { var _a; return (_a = c.getAttribute('data-storepos')) !== null && _a !== void 0 ? _a : ''; });
                    let track = this.tracks.find(t => t.name === sps[0].split(',')[0]);
                    let allSameTrack = sps.every(sp => sp.split(',')[0] === (track === null || track === void 0 ? void 0 : track.name));
                    if (allSameTrack && track && (track === null || track === void 0 ? void 0 : track.positions)) {
                        let positions = (_b = (_a = track.positions) === null || _a === void 0 ? void 0 : _a.filter(p => sps.find(sp => sp.split(',')[1] === p))) !== null && _b !== void 0 ? _b : [];
                        let startIndex = (_d = (_c = track.positions) === null || _c === void 0 ? void 0 : _c.indexOf(positions[0])) !== null && _d !== void 0 ? _d : 0;
                        let consecutivePositions = true;
                        for (let i = startIndex; i < startIndex + positions.length - 1; i++) {
                            if ((track.positions.length - 1) >= i + 1 && !positions.find(p => { var _a; return p === ((_a = track === null || track === void 0 ? void 0 : track.positions) === null || _a === void 0 ? void 0 : _a[i + 1]); })) {
                                consecutivePositions = false;
                                break;
                            }
                        }
                        if (consecutivePositions)
                            context_.push({ menuTxt: this.translateTxt('flipWagons'), eventTxt: 'flipWagons' });
                    }
                }
            }
            if (Array.isArray(this.contextMenu)) {
                this.contextMenu.forEach(cont => {
                    const ctx = { menuTxt: cont.menuTxt, eventTxt: cont.eventTxt };
                    if (cont.eventFncKey)
                        ctx.eventFncKey = cont.eventFncKey;
                    if (cont.subContext)
                        ctx.subContext = cont.subContext;
                    context_.push(ctx);
                });
            }
            this.shuntViewContext.data = {
                clickEvent: e,
                context: context_,
            };
        }
    }
    handleCellClick(e) {
        let cell = e.composedPath().find((el) => {
            return el instanceof HTMLElement && el.classList.contains('cell');
        });
        Array.from(this.allCells).forEach(c => {
            c.classList.remove('no-drop', 'dragover');
        });
        if (cell) {
            if (e.ctrlKey) {
                if (this.selectedCells.length === 0)
                    this.toggleSelection(cell);
                else if (this.sameType(cell, this.selectedCells[0]))
                    this.toggleSelection(cell);
                else {
                    this.clearSelection();
                    this.toggleSelection(cell);
                }
            }
            else if (e.shiftKey) {
                if (this.selectedCells.length === 0)
                    this.toggleSelection(cell);
                else if (this.lastSelectedCell && this.sameType(cell, this.lastSelectedCell)) {
                    let tmpArr = Array.from(this.allCells);
                    let diff = Math.abs(tmpArr.indexOf(this.lastSelectedCell) - tmpArr.indexOf(cell));
                    let startIndex = tmpArr.indexOf(this.lastSelectedCell) < tmpArr.indexOf(cell) ? tmpArr.indexOf(this.lastSelectedCell) : tmpArr.indexOf(cell);
                    tmpArr.splice(0, startIndex);
                    tmpArr.length = diff + 1;
                    tmpArr.forEach(c => {
                        if (this.sameType(c, cell))
                            this.addToSelection(c);
                    });
                }
                else {
                    this.clearSelection();
                    this.toggleSelection(cell);
                }
            }
            else {
                if (this.selectedCells.length === 1 && this.selectedCells[0] === cell)
                    this.clearSelection();
                else {
                    this.clearSelection();
                    this.toggleSelection(cell);
                }
            }
            this.lastSelectedCell = cell;
        }
        this.submitValue = this.submitValFromSelectedCells();
        this.requestUpdate();
    }
    sameType(cell1, cell2) {
        return (cell1.classList.contains('wagon') === cell2.classList.contains('wagon') && cell1.classList.contains('planned') === cell2.classList.contains('planned') && cell1.classList.contains('reserved') === cell2.classList.contains('reserved'));
    }
    addToSelection(cell) {
        if (!this.selectedCells.includes(cell))
            this.selectedCells.push(cell);
        cell.classList.add('selected');
    }
    toggleSelection(cell) {
        if (this.selectedCells.includes(cell)) {
            this.selectedCells.splice(this.selectedCells.indexOf(cell), 1);
        }
        else {
            this.selectedCells.push(cell);
        }
        cell.classList.toggle('selected');
    }
    clearSelection() {
        this.selectedCells.forEach(cell => {
            cell.classList.remove('selected');
        });
        this.selectedCells = [];
    }
    dragHandler(e, track, position) {
        var _a, _b, _c;
        let eventType = e.type;
        if (eventType === 'dragstart') {
            let draggedCell = e.composedPath().find((el) => {
                return el instanceof HTMLElement && el.classList.contains('cell');
            });
            if (draggedCell) {
                if (!this.selectedCells.includes(draggedCell)) {
                    this.clearSelection();
                    this.toggleSelection(draggedCell);
                }
            }
        }
        Array.from(this.allCells).forEach(c => {
            c.classList.remove('no-drop', 'dragover');
        });
        if (eventType === 'dragenter') {
            let cellsOnTrack = Array.from(this.allCells).filter(c => { var _a; return ((_a = c.getAttribute('data-storepos')) === null || _a === void 0 ? void 0 : _a.split(',')[0]) === (track.name); });
            let cellIndex = (_c = (_b = (_a = this.tracks.find(t => t.name === track.name)) === null || _a === void 0 ? void 0 : _a.positions) === null || _b === void 0 ? void 0 : _b.indexOf(position)) !== null && _c !== void 0 ? _c : 0;
            let emptyCellsOnTrack = cellsOnTrack.slice(cellIndex).filter(c => !c.classList.contains('planned') && !c.classList.contains('wagon'));
            if (emptyCellsOnTrack.length < this.selectedCells.length) {
                let cellsToColor = cellsOnTrack.slice(cellIndex);
                cellsToColor.length = Math.min(this.selectedCells.length, cellsToColor.length);
                cellsToColor.forEach(c => {
                    c.classList.add('no-drop');
                });
            }
            else {
                emptyCellsOnTrack.length = this.selectedCells.length;
                emptyCellsOnTrack.forEach(c => {
                    c.classList.add('dragover');
                });
            }
        }
    }
    dropHandler(e) {
        let isAllowed = false;
        let cell = e.composedPath().find((el) => {
            return el instanceof HTMLElement && el.classList.contains('cell');
        });
        if (cell && cell.classList.contains('dragover'))
            isAllowed = true;
        if (isAllowed && this.selectedCells.length > 0) {
            this.submitValue = this.shuntValFromSelectedCells();
            submitEventHandler(undefined, 'requestShunting');
            this.clearSelection();
        }
        Array.from(this.allCells).forEach(c => {
            c.classList.remove('no-drop', 'dragover');
        });
    }
    toggleInfo() {
        this.infoVisible = !this.infoVisible;
        this.requestUpdate();
    }
    parseStorePos(storePos) {
        let parts = storePos.split(',');
        if (parts.length > 1) {
            let track = parts[parts.length - 2];
            let position = parts[parts.length - 1];
            return { track, position };
        }
        return storePos;
    }
    getTrainBlockStyle(block) {
        return {
            'width': `${block.continuousLength * 82 - (block.trainNo ? 11 : 15)}px`,
        };
    }
    formatTrainData(trainNo) {
        let trainData = this.trains.find(train => train.trainNo === trainNo);
        if (trainData) {
            let data = trainNo;
            if (trainData.trainDate)
                data += ` ${trainData.trainDate.toLocaleDateString()}`;
            if (trainData.arrDate)
                data += ` - ${this.translateTxt('arrTime')} ${trainData.arrDate.getHours().toString().padStart(2, '0')}:${(trainData.arrDate.getMinutes().toString().padStart(2, '0'))}`;
            if (trainData.depDate)
                data += ` - ${this.translateTxt('depTime')} ${trainData.depDate.getHours().toString().padStart(2, '0')}:${(trainData.depDate.getMinutes().toString().padStart(2, '0'))}`;
            if (trainData.status)
                data += ` - ${trainData.status}`;
            return data;
        }
        return trainNo;
    }
    getTrainBlocks(positions, wagons) {
        const blocks = [];
        positions.forEach((position) => {
            const wagon = wagons.find(wgn => wgn.storePos.position === position);
            const trainNo = wagon === null || wagon === void 0 ? void 0 : wagon.trainNo;
            const last = blocks[blocks.length - 1];
            if (last && last.trainNo === trainNo)
                last.continuousLength += 1;
            else
                blocks.push({ trainNo, continuousLength: 1 });
        });
        return blocks;
    }
    renderTrainRowCells(track, wagons) {
        var _a, _b;
        let trainWagonsOnTrack = (_a = wagons.filter(wagon => wagon.trainNo && wagon.trainNo.length > 0)) !== null && _a !== void 0 ? _a : [];
        if (trainWagonsOnTrack.length === 0)
            return nothing;
        let positions = (_b = (track.positions ? track.positions : this.positions)) !== null && _b !== void 0 ? _b : Array.from({ length: DEFAULT_POSITIONS }, (_, i) => 'Pos ' + String(i + 1).padStart(2, '0'));
        const blocks = this.getTrainBlocks(positions, wagons);
        return html `
            <tr class="train-row">
                <td class="pic-body2"></td>
                ${blocks.map(block => {
            var _a;
            return html `
                <td class="pic-body2 train-block" colspan=${block.continuousLength}>
                    <span 
                    style=${styleMap(this.getTrainBlockStyle(block))}
                    class=${block.trainNo ? 'train-block-span' : ''}
                    >${this.formatTrainData((_a = block.trainNo) !== null && _a !== void 0 ? _a : '')}</span>
                    ${block.trainNo ? html `<pic-tooltip>${this.formatTrainData(block.trainNo)}</pic-tooltip>` : ''}
                </td>`;
        })}
            </tr>
        `;
    }
    getPosClass(wagon, move) {
        var _a, _b, _c;
        return {
            'pic-body2': true,
            'cell': true,
            'reserved': (move !== undefined && move.wagonId.length === 0),
            'planned': (move !== undefined && move.wagonId.length > 0),
            'wagon': wagon !== undefined,
            'locomotive': Boolean(wagon && (wagon === null || wagon === void 0 ? void 0 : wagon.isLocomotive)),
            'dmg-mild': Boolean((_a = wagon === null || wagon === void 0 ? void 0 : wagon.damage) === null || _a === void 0 ? void 0 : _a.some(d => d.type === 'mild')),
            'dmg-moderate': Boolean((_b = wagon === null || wagon === void 0 ? void 0 : wagon.damage) === null || _b === void 0 ? void 0 : _b.some(d => d.type === 'moderate')),
            'dmg-severe': Boolean((_c = wagon === null || wagon === void 0 ? void 0 : wagon.damage) === null || _c === void 0 ? void 0 : _c.some(d => d.type === 'severe')),
        };
    }
    formatShortDisplay(id) {
        if (!id || id.length === 0) {
            console.error(this.id_, 'Wagon number empty');
            return '';
        }
        if (id.length > 5) {
            const last5 = id.slice(-5);
            return `${last5.slice(0, 4)}-${last5.slice(4)}`;
        }
        return id;
    }
    formatTooltip(id) {
        if (/^\d{12}$/.test(id)) {
            return `${id.slice(0, 2)} ${id.slice(2, 4)} ${id.slice(4, 8)} ${id.slice(8, 11)}-${id.slice(11)}`;
        }
        return id;
    }
    renderTrackRowCells(track, wagons) {
        var _a, _b;
        let trackmoves = (_a = this.shuntingPlans.flatMap(plan => plan.moves.filter(move => move.toLoc && typeof move.toLoc === 'object' && 'track' in move.toLoc && move.toLoc.track === track.name))) !== null && _a !== void 0 ? _a : [];
        let positions = (_b = (track.positions ? track.positions : this.positions)) !== null && _b !== void 0 ? _b : Array.from({ length: DEFAULT_POSITIONS }, (_, i) => 'Pos ' + String(i + 1).padStart(2, '0'));
        return html `
            ${positions.map(position => {
            var _a, _b, _c, _d, _e;
            let wagon = wagons.find(wgn => wgn.storePos.position === position);
            let move = trackmoves.find(mv => mv.toLoc.position === position);
            return html `
                <td
                class=${classMap(this.getPosClass(wagon, move))}
                .item=${wagon}
                data-move-id=${move ? (_a = move.wagonId) !== null && _a !== void 0 ? _a : nothing : nothing}
                data-storepos=${track.name + ',' + position}
                draggable=${wagon !== undefined || (move && move.wagonId && move.wagonId.length > 0) ? 'true' : 'false'}
                @contextmenu=${(e) => this.handleContextMenu(e, wagon, move)}
                @click=${(e) => this.handleCellClick(e)}
                @dragstart=${(e) => this.dragHandler(e, track, position)}
                @dragenter=${(e) => this.dragHandler(e, track, position)}
                @drop=${(e) => this.dropHandler(e)}
                >
                <span>${wagon ? this.formatShortDisplay(wagon.wagonNo) : (move ? this.formatShortDisplay(move.wagonId) : '')}</span>
                ${(wagon || move) ? html `<pic-tooltip>${this.formatTooltip((_c = (_b = wagon === null || wagon === void 0 ? void 0 : wagon.wagonNo) !== null && _b !== void 0 ? _b : move === null || move === void 0 ? void 0 : move.wagonId) !== null && _c !== void 0 ? _c : '') + (((_d = wagon === null || wagon === void 0 ? void 0 : wagon.damage) === null || _d === void 0 ? void 0 : _d.some(d => d.txt.length > 0)) ? ` - ${(_e = wagon === null || wagon === void 0 ? void 0 : wagon.damage) === null || _e === void 0 ? void 0 : _e.filter(d => d.txt.length > 0).map(d => d.txt).join(', ')}` : '')}</pic-tooltip>` : ''}
                </td>`;
        })}
        `;
    }
    renderGapRow() {
        return html `
            <tr class="track-gap">
                <td 
                @mouseenter=${(e) => { Array.from(this.allCells).forEach(c => { c.classList.remove('no-drop', 'dragover'); }); }}
                colspan=${1 + (this.positions.length > 0 ? this.positions.length : DEFAULT_POSITIONS)}></td>
            </tr>
        `;
    }
    renderInfoRowCells(track, wagons) {
        var _a;
        let positions = (_a = (track.positions ? track.positions : this.positions)) !== null && _a !== void 0 ? _a : Array.from({ length: DEFAULT_POSITIONS }, (_, i) => 'Pos ' + String(i + 1).padStart(2, '0'));
        return html `
        <tr class="info-row">
            <td class="info-cell pic-body2"></td>
            ${positions.map(position => {
            var _a;
            let wagon = wagons.find(wgn => wgn.storePos.position === position);
            return html `
                <td class="info-cell cell pic-body2">
                    <span
                    style="text-overflow: ellipsis; white-space: nowrap; overflow: hidden;"
                    >${wagon ? (_a = wagon.comments) !== null && _a !== void 0 ? _a : '' : ''}</span>
                    ${wagon && wagon.comments ? html `<pic-tooltip>${wagon.comments}</pic-tooltip>` : ''}
                </td>
                `;
        })}
        </tr>
        `;
    }
    setComponentInlineStyle() {
        this.style.setProperty('max-width', this.width);
        this.style.setProperty('max-height', this.height);
        this.style.setProperty('--shuntview-width', this.width);
        this.style.setProperty('--shuntview-height', this.height);
    }
    render() {
        this.setComponentInlineStyle();
        let infoToggleAction = new PICAction();
        infoToggleAction.type_ = 'internal';
        infoToggleAction.eventFnc = this.toggleInfo.bind(this);
        return html `
            <div class="outerContainer">
                <pic-checkbox
                class="info-toggle"
                .isinput__=${false}
                .width=${'60px'}
                .tooltip_=${'Show info'}
                .density=${'high'}
                .eType=${'switch'}
                .label=${'Show info'}
                .value=${'false'}
                .clickAction=${infoToggleAction}
                >
                </pic-checkbox>
                <table class="table"
                @contextmenu=${(e) => e.button > 0 ? e.preventDefault() : nothing}>
                    <thead>
                        <tr>
                            <th class="pic-body2 header">${this.translateTxt('track')}</th>
                            ${this.positions.length > 0 ?
            this.positions.map(position => html `<th class="pic-body2 header">${position}</th>`)
            : Array.from({ length: DEFAULT_POSITIONS }, (_, i) => html `<th class="pic-body2 header">Pos ${String(i + 1).padStart(2, '0')}</th>`)}
                        </tr>
                    </thead>
                    <tbody>
                        ${this.tracks.map((track, index) => {
            var _a;
            let wagonsOnTrack = (_a = this.wagons.filter(wagon => wagon.storePos && typeof wagon.storePos === 'object' && 'track' in wagon.storePos && wagon.storePos.track === track.name)) !== null && _a !== void 0 ? _a : [];
            return html `
                                ${index === 0 && wagonsOnTrack.filter(wagon => wagon.trainNo && wagon.trainNo.length > 0).length > 0 ? nothing : this.renderGapRow()}
                                ${this.trains.length > 0 ? this.renderTrainRowCells(track, wagonsOnTrack) : nothing}
                                <tr>
                                    <td class="pic-body2 header">
                                        <span>${track.name}</span>
                                        ${track.length ? html `<pic-tooltip>${track.length} m</pic-tooltip>` : ''}
                                    </td>
                                    ${this.renderTrackRowCells(track, wagonsOnTrack)}
                                </tr>
                                ${this.infoVisible && wagonsOnTrack.length > 0 ? this.renderInfoRowCells(track, wagonsOnTrack) : nothing}
                            `;
        })}
                        ${this.renderGapRow()}

                    </tbody>
                </table>
            </div>
            <pic-context id="shuntViewContext" mode="table"></pic-context>
        `;
    }
};
__decorate([
    property({ converter: stringDecoder, attribute: 'stationname' })
], PICShuntView.prototype, "stationName", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: 'locationcode' })
], PICShuntView.prototype, "locationCode", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: 'countrycode' })
], PICShuntView.prototype, "countryCode", void 0);
__decorate([
    property({ type: String, attribute: 'height' })
], PICShuntView.prototype, "height", null);
__decorate([
    property({ type: String, attribute: 'width' })
], PICShuntView.prototype, "width", null);
__decorate([
    query('#shuntViewContext')
], PICShuntView.prototype, "shuntViewContext", void 0);
__decorate([
    queryAll('.cell')
], PICShuntView.prototype, "allCells", void 0);
__decorate([
    queryAll('.cell:not(.wagon):not(.planned)')
], PICShuntView.prototype, "allEmptyCells", void 0);
__decorate([
    queryAll('.cell.wagon')
], PICShuntView.prototype, "allWagons", void 0);
__decorate([
    queryAll('.cell.planned')
], PICShuntView.prototype, "allPlanned", void 0);
PICShuntView = __decorate([
    customElement('pic-shuntview')
], PICShuntView);
export { PICShuntView };
//# sourceMappingURL=PICShuntView.js.map